<script setup>
import {ref, watch, computed, onMounted, onBeforeUnmount} from 'vue';
import { X, ArrowRight } from 'lucide-vue-next';
import { usePage } from '@inertiajs/vue3';
import {trackEvent, trackFathomEvent} from '@/Helpers/analytics';
import {useUpgradeOptions} from "@/composables/useUpgradeOptions";

const { clearUpgradeOptions } = useUpgradeOptions()

const props = defineProps({
    isOpen: Boolean,
    source: {
        type: String,
        default: 'casting_upgrade_modal'
    },
    source2: {
        type: String,
        default: 'casting_upgrade_modal'
    },
});
const emit = defineEmits(['close']);

const isVisible = ref(false);

watch(() => props.isOpen, (open) => {
    isVisible.value = open;
}, { immediate: true });

const upgradeOptions = usePage().props.upgradeOptions;

const monthly = computed(() => upgradeOptions?.button_monthly ?? null);
const yearly = computed(() => upgradeOptions?.button_yearly ?? null);

const yearlyDealPercent = computed(() => {
    const deal = yearly.value?.data?.deal;
    if (!deal) return null;
    const match = deal.match(/(\d+)%/);
    return match ? `${match[1]}%` : null;
});

const handleUpgrade = (option, label) => {
    if (!option?.url) return;

    localStorage.setItem('upgrade_source', props.source);
    localStorage.setItem('upgrade_type', label.toLowerCase());
    localStorage.setItem('upgrade_url', option.url);

    trackFathomEvent(`${props.source2} Upgrade ${label} Clicked`);
    trackEvent(`${props.source2} Upgrade ${label} Clicked`)

    window.location.href = option.url;
};

onMounted(() => {
    clearUpgradeOptions();
})

const handleClose = () => {
    emit('close');
    clearUpgradeOptions();
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="fixed inset-0 z-[51] flex items-center justify-center p-4 sm:p-6"
        >
            <!-- Backdrop -->
            <div
                class="absolute inset-0 bg-black/85 backdrop-blur-sm transition-opacity duration-300"
                :class="isVisible ? 'opacity-100' : 'opacity-0'"
                aria-hidden="true"
            />

            <!-- Modal -->
            <div
                class="relative w-full max-w-[380px] transform rounded-2xl bg-zinc-950 border border-zinc-800 shadow-2xl transition-all duration-300 ease-out"
                :class="isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'"
                role="dialog"
                aria-modal="true"
                aria-labelledby="upgrade-modal-title"
            >
                <!-- Close Button -->
                <button
                    class="absolute right-3 top-3 p-1.5 text-zinc-600 hover:text-white transition-colors rounded-full hover:bg-zinc-800 z-10"
                    aria-label="Close modal"
                    @click="handleClose"
                >
                    <X class="h-4 w-4" />
                </button>

                <div class="p-6">
                    <!-- Header -->
                    <div class="text-center mb-6 pr-4">
                        <h2
                            id="upgrade-modal-title"
                            class="text-2xl font-bold text-white mb-2"
                        >
                            Unlock Premium Access
                        </h2>
                        <p class="text-zinc-400 text-sm leading-relaxed">
                            Pitching is a <span class="text-white font-medium">Premium feature</span>. Upgrade to submit your scene and watch it get filmed.
                        </p>
                    </div>

                    <!-- Pricing Cards -->
                    <div class="space-y-3 pt-3">

                        <!-- Annual (featured) -->
                        <div v-if="yearly" class="relative">
                            <!-- BEST DEAL badge -->
                            <div class="absolute -top-3 left-4 z-10 bg-red-600 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-md uppercase tracking-wide">
                                Best Deal
                            </div>
                            <div class="rounded-xl border border-blue-500/50 bg-gradient-to-br from-blue-950/60 via-zinc-900 to-zinc-900 p-4 pt-5" style="backdrop-filter: blur(12px)">
                                <!-- Plan label -->
                                <p class="text-blue-400 text-[11px] font-bold uppercase tracking-widest mb-2.5">
                                    {{ yearly.data.name }}<span v-if="yearlyDealPercent"> · Save {{ yearlyDealPercent }}</span>
                                </p>
                                <!-- Price row -->
                                <div class="flex items-baseline gap-2 mb-1.5">
                                    <span class="text-white font-bold text-4xl leading-none">{{ yearly.data.price }}{{ yearly.data.cents }}</span>
                                    <span class="text-zinc-400 text-sm">{{ yearly.data.period }}</span>
                                    <span class="diagonal-strike text-zinc-500 text-sm">{{ yearly.data.strike_price }}</span>
                                </div>
                                <!-- Feature + billing -->
                                <p class="text-zinc-400 text-xs mb-4">
                                    {{ yearly.data.text }}<span v-if="yearly.data.new_bill_amount"> · {{ yearly.data.new_bill_amount }} billed once a year</span>
                                </p>
                                <!-- CTA -->
                                <button
                                    class="w-full py-3.5 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-500 hover:from-blue-500 hover:via-blue-400 hover:to-indigo-400 text-white font-bold text-sm rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 uppercase tracking-wide shadow-lg shadow-blue-600/30"
                                    @click="handleUpgrade(yearly, 'Yearly')"
                                >
                                    Get Annual<span v-if="yearlyDealPercent"> — Save {{ yearlyDealPercent }}</span>
                                    <ArrowRight class="w-4 h-4 shrink-0" />
                                </button>
                            </div>
                        </div>

                        <!-- Monthly -->
                        <div
                            v-if="monthly"
                            class="rounded-xl border border-zinc-700/60 bg-zinc-900/40 p-4"
                        >
                            <!-- Plan label -->
                            <p class="text-zinc-500 text-[11px] font-bold uppercase tracking-widest mb-2.5">
                                {{ monthly.data.name }}
                            </p>
                            <!-- Price row -->
                            <div class="flex items-baseline gap-2 mb-1.5">
                                <span class="text-white font-bold text-4xl leading-none">{{ monthly.data.price }}{{ monthly.data.cents }}</span>
                                <span class="text-zinc-400 text-sm">{{ monthly.data.period }}</span>
                                <span class="diagonal-strike text-zinc-500 text-sm">{{ monthly.data.strike_price }}</span>
                            </div>
                            <!-- Feature -->
                            <p class="text-zinc-400 text-xs mb-4">{{ monthly.data.text }}</p>
                            <!-- CTA -->
                            <button
                                class="w-full py-3.5 border border-blue-500/50 text-blue-400 hover:bg-blue-500/10 font-bold text-sm rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2 uppercase tracking-wide"
                                @click="handleUpgrade(monthly, 'Monthly')"
                            >
                                Get Monthly
                                <ArrowRight class="w-4 h-4 shrink-0" />
                            </button>
                        </div>
                    </div>

                    <!-- Footer -->
                    <p class="text-[11px] text-zinc-600 text-center mt-4 leading-relaxed">
                        Cancel anytime. Billed automatically.
                    </p>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
.diagonal-strike {
    position: relative;
    display: inline-block;
}
.diagonal-strike::after {
    content: '';
    position: absolute;
    left: -1px;
    right: -1px;
    top: 50%;
    height: 1.5px;
    background: #ef4444;
    transform: translateY(-50%) rotate(-8deg);
}
</style>
